import { useState, useMemo, useEffect, useRef } from 'react';
import { Cron } from 'croner';

interface ScheduleEditorProps {
  value: string;
  onChange: (cronExpression: string) => void;
}

interface ScheduleState {
  enabled: boolean;
  days: boolean[]; // [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
  times: { hour: number; minute: number }[];
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Parse a cron expression into schedule state
 */
function parseCronToSchedule(cronExpression: string): ScheduleState {
  if (!cronExpression || cronExpression.trim() === '') {
    return {
      enabled: false,
      days: [false, false, false, false, false, false, false],
      times: [{ hour: 8, minute: 0 }],
    };
  }

  try {
    const parts = cronExpression.trim().split(/\s+/);
    if (parts.length !== 5) {
      return getDefaultSchedule();
    }

    const [minutePart, hourPart, , , dayOfWeekPart] = parts;

    // Parse minutes (take first value if multiple)
    const minutes = minutePart.split(',').map(m => Number.parseInt(m, 10));
    const minute = minutes[0] || 0;

    // Parse hours
    const hours = hourPart.split(',').map(h => Number.parseInt(h, 10)).filter(h => !Number.isNaN(h));

    // Build times array
    const times = hours.length > 0
      ? hours.slice(0, 2).map(hour => ({ hour, minute }))
      : [{ hour: 8, minute }];

    // Parse days of week
    const days = [false, false, false, false, false, false, false];

    if (dayOfWeekPart === '*') {
      // Every day
      days.fill(true);
    } else {
      // Parse specific days
      const dayParts = dayOfWeekPart.split(',');
      for (const part of dayParts) {
        if (part.includes('-')) {
          // Range like 1-5
          const [start, end] = part.split('-').map(d => Number.parseInt(d, 10));
          for (let i = start; i <= end; i++) {
            if (i >= 0 && i <= 6) days[i] = true;
          }
        } else {
          const dayNum = Number.parseInt(part, 10);
          if (dayNum >= 0 && dayNum <= 6) {
            days[dayNum] = true;
          }
        }
      }
    }

    return { enabled: true, days, times };
  } catch {
    return getDefaultSchedule();
  }
}

function getDefaultSchedule(): ScheduleState {
  return {
    enabled: true,
    days: [false, true, true, true, true, true, false], // Mon-Fri
    times: [{ hour: 8, minute: 0 }],
  };
}

/**
 * Convert schedule state to a cron expression
 */
function scheduleToCron(schedule: ScheduleState): string {
  if (!schedule.enabled || schedule.times.length === 0) {
    return '';
  }

  // Get selected days
  const selectedDays = schedule.days
    .map((selected, index) => (selected ? index : -1))
    .filter(d => d !== -1);

  if (selectedDays.length === 0) {
    return '';
  }

  // Use the minute from the first time entry
  const minute = schedule.times[0].minute;

  // Get hours from all time entries
  const hours = schedule.times.map(t => t.hour).join(',');

  // Build day of week field
  const dayOfWeek = selectedDays.length === 7 ? '*' : selectedDays.join(',');

  return `${minute} ${hours} * * ${dayOfWeek}`;
}

/**
 * Get the next N scheduled run times
 */
function getNextRuns(cronExpression: string, count: number): Date[] {
  if (!cronExpression) return [];

  try {
    const cron = new Cron(cronExpression);
    const runs: Date[] = [];
    let nextRun = cron.nextRun();

    for (let i = 0; i < count && nextRun; i++) {
      runs.push(nextRun);
      nextRun = cron.nextRun(nextRun);
    }

    return runs;
  } catch {
    return [];
  }
}

/**
 * Format a date for display
 */
function formatDateTime(date: Date): string {
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * Get the browser's timezone name
 */
function getTimezoneName(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format time for input value
 */
function formatTimeForInput(hour: number, minute: number): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

export function ScheduleEditor({ value, onChange }: ScheduleEditorProps) {
  const [schedule, setSchedule] = useState<ScheduleState>(() => parseCronToSchedule(value));
  const [showNextRuns, setShowNextRuns] = useState(false);

  // Refs to avoid stale closures and prevent effect dependency churn
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Track the last value we propagated to parent to distinguish external vs internal changes
  const lastSentValue = useRef(value);

  // Sync FROM parent only when value changes externally (not from our own onChange)
  useEffect(() => {
    if (value !== lastSentValue.current) {
      setSchedule(parseCronToSchedule(value));
      lastSentValue.current = value;
    }
  }, [value]);

  const cronExpression = useMemo(() => scheduleToCron(schedule), [schedule]);

  // Sync TO parent when internal state produces a different cron expression
  useEffect(() => {
    if (cronExpression !== value) {
      lastSentValue.current = cronExpression;
      onChangeRef.current(cronExpression);
    }
  }, [cronExpression, value]);

  const nextRuns = useMemo(() => getNextRuns(cronExpression, 10), [cronExpression]);
  const timezone = useMemo(() => getTimezoneName(), []);

  const toggleDay = (index: number) => {
    setSchedule(prev => ({
      ...prev,
      days: prev.days.map((d, i) => (i === index ? !d : d)),
    }));
  };

  const updateTime = (index: number, timeString: string) => {
    if (!timeString) return;
    const [hour, minute] = timeString.split(':').map(n => Number.parseInt(n, 10));
    setSchedule(prev => ({
      ...prev,
      times: prev.times.map((t, i) => (i === index ? { hour, minute } : t)),
    }));
  };

  const addSecondTime = () => {
    if (schedule.times.length < 2) {
      // Default to 12 hours after first time
      const firstTime = schedule.times[0];
      const newHour = (firstTime.hour + 12) % 24;
      setSchedule(prev => ({
        ...prev,
        times: [...prev.times, { hour: newHour, minute: firstTime.minute }],
      }));
    }
  };

  const removeSecondTime = () => {
    setSchedule(prev => ({
      ...prev,
      times: prev.times.slice(0, 1),
    }));
  };

  const toggleEnabled = () => {
    setSchedule(prev => ({
      ...prev,
      enabled: !prev.enabled,
    }));
  };

  const selectAllDays = () => {
    setSchedule(prev => ({
      ...prev,
      days: prev.days.map(() => true),
    }));
  };

  const selectWeekdays = () => {
    setSchedule(prev => ({
      ...prev,
      days: [false, true, true, true, true, true, false],
    }));
  };

  const selectedDaysCount = schedule.days.filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center gap-3">
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={schedule.enabled}
            onChange={toggleEnabled}
            className="peer sr-only"
          />
          <div className="h-6 w-11 rounded-full bg-gray-700 peer-checked:bg-indigo-600 peer-focus:ring-2 peer-focus:ring-indigo-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:after:translate-x-full" />
        </label>
        <span className="text-sm text-gray-300">
          {schedule.enabled ? 'Automatic scheduling enabled' : 'Automatic scheduling disabled'}
        </span>
      </div>

      {schedule.enabled && (
        <>
          {/* Timezone Display */}
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Timezone: {timezone}</span>
          </div>

          {/* Days of Week Selection */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="label">Days of Week</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectWeekdays}
                  className="text-xs text-indigo-400 hover:text-indigo-300"
                >
                  Weekdays
                </button>
                <span className="text-gray-600">|</span>
                <button
                  type="button"
                  onClick={selectAllDays}
                  className="text-xs text-indigo-400 hover:text-indigo-300"
                >
                  Every day
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day, index) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(index)}
                  title={DAYS_FULL[index]}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                    schedule.days[index]
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
            {selectedDaysCount === 0 && (
              <p className="mt-1 text-xs text-amber-400">Select at least one day</p>
            )}
          </div>

          {/* Time Selection */}
          <div>
            <span className="label">Time of Day</span>
            <div className="space-y-3">
              {schedule.times.map((time, index) => (
                <div key={`time-${index === 0 ? 'first' : 'second'}`} className="flex items-center gap-3">
                  <input
                    type="time"
                    value={formatTimeForInput(time.hour, time.minute)}
                    onChange={(e) => updateTime(index, e.target.value)}
                    className="input w-40"
                  />
                  {index === 0 && schedule.times.length === 1 && (
                    <button
                      type="button"
                      onClick={addSecondTime}
                      className="text-sm text-indigo-400 hover:text-indigo-300"
                    >
                      + Add second time
                    </button>
                  )}
                  {index === 1 && (
                    <button
                      type="button"
                      onClick={removeSecondTime}
                      className="text-sm text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {schedule.times.length === 1
                ? 'Runs once daily at this time'
                : 'Runs twice daily at these times'}
            </p>
          </div>

          {/* Schedule Summary */}
          {cronExpression && (
            <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-300">Schedule Summary</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {selectedDaysCount === 7 ? 'Every day' :
                      `${schedule.days.map((d, i) => d ? DAYS_OF_WEEK[i] : '').filter(Boolean).join(', ')}`}
                    {' at '}
                    {schedule.times.map(t =>
                      `${t.hour.toString().padStart(2, '0')}:${t.minute.toString().padStart(2, '0')}`
                    ).join(' and ')}
                  </div>
                </div>

                {/* Info Icon for Next Runs */}
                <div className="relative">
                  <button
                    type="button"
                    onMouseEnter={() => setShowNextRuns(true)}
                    onMouseLeave={() => setShowNextRuns(false)}
                    onFocus={() => setShowNextRuns(true)}
                    onBlur={() => setShowNextRuns(false)}
                    className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-300"
                    aria-label="Show next scheduled runs"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>

                  {/* Tooltip with Next Runs */}
                  {showNextRuns && nextRuns.length > 0 && (
                    <div className="absolute bottom-full right-0 mb-2 z-50 w-72 rounded-lg border border-gray-600 bg-gray-800 p-3 shadow-xl">
                      <div className="mb-2 text-sm font-medium text-gray-300">
                        Next 10 Scheduled Runs
                      </div>
                      <ul className="space-y-1 text-xs text-gray-400">
                        {nextRuns.map((run) => (
                          <li key={run.toISOString()} className="flex items-center gap-2">
                            {formatDateTime(run)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
